import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState, fmtDate } from "../components/common";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Buildings, Plus, Target, Scan } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Workspaces() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", client_name: "", description: "" });
  const canManage = user.role === "admin" || user.role === "pentester";

  const load = () => api.get("/workspaces").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/workspaces", form);
      toast.success("Workspace created");
      setOpen(false);
      setForm({ name: "", client_name: "", description: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="fade-up">
      <PageHeader
        overline="Multi-tenant"
        title="Workspaces"
        subtitle="Isolated per-client environments. Clients only ever see their own workspace."
        action={
          canManage && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-workspace-button" className="bg-blue-600 hover:bg-blue-500 text-white">
                  <Plus size={17} weight="bold" className="mr-1.5" /> New Workspace
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0b1220] border-[#1e293b] text-white">
                <DialogHeader><DialogTitle>Create Workspace</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-slate-300">Workspace name</Label>
                    <Input data-testid="workspace-name-input" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-1 bg-[#0f172a] border-[#1e293b]" placeholder="Acme Corp - Prod" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Client name</Label>
                    <Input data-testid="workspace-client-input" value={form.client_name}
                      onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                      className="mt-1 bg-[#0f172a] border-[#1e293b]" placeholder="Acme Corporation" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Description</Label>
                    <Textarea data-testid="workspace-desc-input" value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="mt-1 bg-[#0f172a] border-[#1e293b]" />
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="workspace-create-submit" onClick={create}
                    disabled={!form.name || !form.client_name}
                    className="bg-blue-600 hover:bg-blue-500 text-white">Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />

      {items.length === 0 ? (
        <EmptyState icon={Buildings} title="No workspaces" hint="Create a workspace to organise targets and scans per client." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((w) => (
            <Card key={w.id} data-testid={`workspace-card-${w.id}`}
              className="bg-[#0f172a] border-[#1e293b] rounded-lg p-5 hover:-translate-y-0.5 hover:border-blue-500/40 transition-transform transition-colors duration-150">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-md bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                  <Buildings size={20} className="text-blue-400" weight="bold" />
                </div>
                <span className="overline text-slate-600">{fmtDate(w.created_at).split(",")[0]}</span>
              </div>
              <h3 className="font-head text-lg font-semibold text-white mt-4">{w.name}</h3>
              <div className="text-sm text-slate-400">{w.client_name}</div>
              {w.description && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{w.description}</p>}
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#1e293b] text-sm text-slate-400">
                <span className="flex items-center gap-1.5"><Target size={15} className="text-blue-500" /> {w.target_count} targets</span>
                <span className="flex items-center gap-1.5"><Scan size={15} className="text-blue-500" /> {w.scan_count} scans</span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
